<template>
    <v-container fluid class="simple-chars-container fill-height d-flex flex-column">
        <!-- Loading state cuando el dispositivo no está disponible -->
        <v-row v-if="!isDeviceReady" class="fill-height">
            <v-col cols="12">
                <v-card outlined class="pa-2 summary-card top-summary summary-skeleton-card">
                    <v-row no-gutters align="stretch" class="summary-row">
                        <v-col cols="12" md="4" class="summary-skeleton-block">
                            <v-skeleton-loader type="heading, text" class="mx-2"></v-skeleton-loader>
                        </v-col>
                        <v-col cols="12" md="4" class="summary-skeleton-block">
                            <v-skeleton-loader type="text, text" class="mx-2"></v-skeleton-loader>
                        </v-col>
                        <v-col cols="12" md="4" class="summary-skeleton-block">
                            <v-skeleton-loader type="text, text" class="mx-2"></v-skeleton-loader>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
            <v-col cols="12" md="6" class="d-flex flex-column">
                <v-skeleton-loader type="image" height="130px" class="mt-2"></v-skeleton-loader>
                <v-skeleton-loader type="image" height="130px" class="mt-2"></v-skeleton-loader>
            </v-col>
            <v-col cols="12" md="6" class="d-flex flex-column">
                <v-skeleton-loader type="image" height="130px" class="mt-2"></v-skeleton-loader>
                <v-skeleton-loader type="image" height="130px" class="mt-2"></v-skeleton-loader>
            </v-col>
        </v-row>

        <!-- Device Chart View (Splitted) -->
        <div v-else-if="isDeviceChart" class="device-chart-row">
            <div class="summary-slot">
                <v-card outlined class="pa-1 summary-card top-summary">
                    <v-row no-gutters align="stretch" class="summary-row">
                        <v-col cols="auto" class="summary-block text-left device-block">
                            <div class="caption grey--text text--darken-1">Dispositivo</div>
                            <div class="text-h4 text--primary device-name">{{ deviceName }}</div>
                        </v-col>

                        <v-col cols="auto" class="summary-block text-left laeq-pack">
                            <v-row no-gutters class="laeq-pack__row align-center">
                                <v-col cols="auto" class="laeq-pack__item">
                                    <div class="metric-title laeq-title">Laeq</div>
                                    <div class="laeq-inline">
                                        <span class="metric-value metric-primary value-underline laeq-value" :style="{ borderBottomColor: headerLaeqColor }">
                                            {{ headerLaeqValue }}
                                        </span>
                                        <span class="laeq-unit">dBA</span>
                                    </div>
                                    <div class="caption grey--text text--darken-1 laeq-time">{{ headerLaeqTime }} hrs</div>
                                </v-col>
                                <v-col cols="auto" class="laeq-pack__item laeq-pack__delta">
                                    <template v-if="weeklyLoading">
                                        <div class="avg-summary-loading">
                                            <v-skeleton-loader type="text" width="92" class="summary-skeleton-line"></v-skeleton-loader>
                                            <v-skeleton-loader type="text" width="124" class="summary-skeleton-line"></v-skeleton-loader>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="avg-delta-split">
                                            <div class="avg-delta-col avg-delta-col--delta" v-if="showWeeklyComparisonDelta">
                                                <div class="laeq-arrow laeq-arrow--value" :class="laeqArrowClass">
                                                    <v-icon small class="laeq-arrow-icon" :class="laeqArrowClass">{{ laeqArrowIcon }}</v-icon>
                                                    {{ laeqDeltaDisplay }} <span class="laeq-delta-unit">dBA</span>
                                                </div>
                                                <div class="laeq-compare delta-note" :class="laeqArrowClass">
                                                    <div>Respecto al promedio</div>
                                                    <div>Últimos 7 días a las {{ weeklyHourMinuteLabel }}</div>
                                                </div>
                                            </div>
                                            <v-divider vertical class="avg-delta-divider" v-if="showWeeklyComparisonDelta"></v-divider>
                                            <div class="avg-delta-col avg-delta-col--avg">
                                                <div class="metric-title laeq-title avg-pack-title">
                                                    <span>Promedio</span>
                                                    <v-tooltip bottom max-width="280">
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-icon x-small class="avg-info-inline" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                                        </template>
                                                        <span>{{ averageTooltipText }}</span>
                                                    </v-tooltip>
                                                </div>
                                                <div class="laeq-inline avg-inline">
                                                    <span class="metric-value metric-primary value-underline avg-value" :style="{ borderBottomColor: headerAverageColor }">
                                                        {{ weeklyMinuteDisplay }}
                                                    </span>
                                                    <span class="laeq-unit">dBA</span>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </v-col>
                            </v-row>
                        </v-col>

                        <v-col cols="auto" class="summary-block right-pack ml-auto">
                            <template v-if="loadingHourly">
                                <div class="right-pack-loading">
                                    <v-skeleton-loader type="text" width="92" class="summary-skeleton-line"></v-skeleton-loader>
                                    <v-skeleton-loader type="text" width="80" class="summary-skeleton-line"></v-skeleton-loader>
                                </div>
                            </template>
                            <template v-else>
                                <div class="right-pack__inner">
                                    <div class="right-pack__item text-center">
                                        <div class="metric-title">Operatividad</div>
                                        <div class="metric-value">{{ headerOperativity }}</div>
                                        <div class="caption grey--text text--darken-1">{{ operativityCaption }}</div>
                                    </div>
                                    <div class="right-pack__item text-center">
                                        <div class="metric-title">Tendencia</div>
                                        <div class="metric-value value-underline" :style="{ borderBottomColor: headerTrendColor }">
                                            {{ headerTrendValue }}
                                        </div>
                                        <div class="caption grey--text text--darken-1" v-if="headerTrendValue !== '--'">{{ headerTrendTime }}</div>
                                    </div>
                                </div>
                            </template>
                        </v-col>
                    </v-row>
                </v-card>
            </div>

            <div class="charts-slot">
                <div class="charts-row">
                    <div class="chart-column">
                        <v-card outlined class="chart-card chart-card--top mb-0" light color="white">
                            <v-card-title class="caption font-weight-medium pa-0 chart-title">
                                LAeq día actual
                            </v-card-title>
                            <v-card-text class="pa-0 chart-body laeq-chart-body top-chart-body">
                                 <template v-if="loadingDailyChart">
                                    <div class="chart-loading-shell">
                                        <v-skeleton-loader type="image" height="100%" class="chart-skeleton-fill"></v-skeleton-loader>
                                    </div>
                                </template>
                                <template v-else>
                                    <apexchart v-if="series.length > 0" ref="chart" height="100%" :options="options" :series="series"></apexchart>
                                     <div v-else class="text-center py-5 caption grey--text">
                                        No hay datos del día actual
                                    </div>
                                </template>
                            </v-card-text>
                        </v-card>

                        <v-card class="chart-card chart-card--bottom elevation-0">
                            <v-card-title class="caption font-weight-medium pa-0 chart-title">
                                LAeq Últimos 7 días
                            </v-card-title>
                            <v-card-text class="pa-0 chart-body laeq-chart-body laeq-weekly-chart-body">
                                <template v-if="loadingWeeklySeries">
                                    <div class="chart-loading-shell">
                                        <v-skeleton-loader type="image" height="100%" class="chart-skeleton-fill"></v-skeleton-loader>
                                    </div>
                                </template>
                                <template v-else>
                                    <apexchart
                                        v-if="weeklySeries.length > 0 && weeklySeries[0].data.length > 0"
                                        ref="weeklyChart"
                                        height="100%"
                                        :options="weeklyOptions"
                                        :series="weeklySeries"
                                    ></apexchart>
                                    <div v-else class="text-center py-5 caption grey--text">
                                        No hay datos de tendencia
                                    </div>
                                </template>
                            </v-card-text>
                        </v-card>
                    </div>

                    <div class="chart-column">
                        <v-card outlined class="chart-card chart-card--top mb-0" light color="white">
                            <v-card-title class="caption font-weight-medium pa-0 chart-title">
                                Tendencias por Hora
                            </v-card-title>
                            <v-card-text class="pa-0 chart-body top-chart-body">
                                 <template v-if="loadingHourly">
                                    <div class="chart-loading-shell">
                                        <v-skeleton-loader type="image" height="100%" class="chart-skeleton-fill"></v-skeleton-loader>
                                    </div>
                                </template>
                                <template v-else>
                                    <apexchart
                                        v-if="dailyTrends.length > 0"
                                        ref="trendChart"
                                        type="bar"
                                        height="100%"
                                        :options="trendOptions"
                                        :series="trendSeries"
                                    ></apexchart>
                                    <div v-else class="text-center py-5">
                                        No hay datos de tendencia
                                    </div>
                                </template>
                            </v-card-text>
                        </v-card>

                        <div class="range-chart">
                            <DashboardRangeTrendChart
                                ref="rangeChart"
                                :statsData="rangeStats"
                                :loading="loadingRange"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="comparison-chart-row">
            <div class="comparison-chart-header">
                <p class="text-h4 text--primary mb-0">
                    Comparación de dispositivos
                </p>
            </div>

            <div v-if="isLoading" class="comparison-chart-loading">
                <v-skeleton-loader type="image" class="comparison-skeleton-fill"></v-skeleton-loader>
                <v-skeleton-loader type="image" class="comparison-skeleton-fill"></v-skeleton-loader>
            </div>

            <div v-else class="comparison-chart-shell">
                <div class="comparison-chart-card">
                    <apexchart
                        ref="chart"
                        height="100%"
                        :options="options"
                        :series="series"
                    ></apexchart>
                </div>
            </div>
        </div>
    </v-container>
</template>

<script>
import moment from 'moment'
import VueApexCharts from 'vue-apexcharts'
import axios from 'axios'
import authHeader from '../../services/authHeader';
import settings from '@/constants/settings.js'
import colors from '@/constants/colorPalette.js'
import DashboardRangeTrendChart from '@/components/components/DashboardRangeTrendChart.vue'

function formatDecibelAxisLabel(value) {
    if (!Number.isFinite(value)) return ''
    return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '')
}

function padHour(value) {
    return String(value).padStart(2, '0')
}

function createApexToolbar() {
    return {
        show: true,
        tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
        },
        autoSelected: 'zoom'
    }
}

function createApexInteractions() {
    return {
        selection: {
            enabled: true
        },
        zoom: {
            enabled: true,
            type: 'x',
            autoScaleYaxis: true
        }
    }
}

export default {
    name: 'SimpleChars',
    props: {
        deviceId: String,
        devices: Array,
        isActive: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        dataLoaded: false,
        dailyTrends: [],
        rangeStats: null,
        loadingDailyChart: false,
        loadingHourly: false,
        loadingRange: false,
        laeqRefreshTimer: null,
        hourlyTrendTimer: null,
        rangeStatsTimer: null,
        weeklyRefreshTimer: null,
        loadingWeeklySeries: false,
        currentLaeqValue: null,
        weeklySeries: [],
        weeklyOptions: {
            colors: colors.CHART_COLORS,
            markers: {
                showNullDataPoints: false,
            },
            stroke: {
                width: [2],
                curve: 'straight'
            },
            chart: {
                type: 'line',
                id: 'weeklyLaeq',
                toolbar: createApexToolbar(),
                parentHeightOffset: 0
            },
            ...createApexInteractions(),
            yaxis: {
                labels: {
                    minWidth: 0,
                    formatter: formatDecibelAxisLabel
                },
                title: {
                    text: 'Decibeles [dBA]',
                },
            },
            xaxis: {
                type: 'datetime',
                tickAmount: 1,
                labels: {
                    show: true,
                    datetimeUTC: false,
                    format: 'dd/MM',
                }
            },
            tooltip: {
                x: {
                    format: "DD/MM HH:mm"
                }
            },
        },
        trendSeries: [],
        trendOptions: {
            chart: {
                type: 'bar',
                toolbar: createApexToolbar(),
                parentHeightOffset: 0
            },
            ...createApexInteractions(),
            colors: ['#FFDBA1'], // Matches DataSound style
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    columnWidth: '60%',
                    dataLabels: {
                        position: 'top'
                    }
                }
            },
            dataLabels: {
                enabled: false,
                offsetY: -6,
                style: {
                    fontSize: '10px',
                    colors: ['#000000']
                }
            },
            xaxis: {
                categories: [],
                labels: {
                    style: {
                        fontSize: '10px'
                    }
                }
            },
            yaxis: {
                title: {
                    text: 'Tendencia (dBA)'
                },
                min: undefined,
                max: undefined,
                labels: {
                    formatter: function (val) {
                        return Math.round(val)
                    }
                }
            },
            tooltip: {
                y: {
                    formatter: function (val) {
                        return val + " dBA"
                    }
                }
            }
        },
        options: {
            colors: colors.CHART_COLORS,
            markers: {
                showNullDataPoints: false,
            },
            stroke: {
                width: [2, 2, 2, 2, 2],
                curve: 'straight'
            },
            chart: {
                type: 'line',
                id: 'hA',
                group: 'social',
                toolbar: createApexToolbar(),
                parentHeightOffset: 0
            },
            ...createApexInteractions(),
            yaxis: {
                labels: {
                    minWidth: 0,
                    formatter: formatDecibelAxisLabel
                },
                title: {
                    text: 'Decibeles [dBA]',
                },
            },
            xaxis: {
                type: 'datetime',
                tickAmount: 1,
                labels: {
                    show: true,
                    datetimeUTC: false,
                    format: 'HH:mm:ss',
                }
            },
            tooltip: {
                x: {
                    format: "HH:mm:ss"
                }
            },
        },
        series: [],
        Laeqfs: [],
        deviceName: '',
        minLaeqfs: 100, // Initialize min value
        layoutResizeTimers: []
    }),

    methods: {
        getApexInstance(refName) {
            const ref = this.$refs[refName]
            if (!ref) return null
            if (Array.isArray(ref) && ref.length > 0) return ref[0].chart || null
            return ref.chart || null
        },

        getApexComponent(refName) {
            const ref = this.$refs[refName]
            if (!ref) return null
            if (Array.isArray(ref) && ref.length > 0) return ref[0] || null
            return ref
        },

        getRangeChartComponent() {
            const ref = this.$refs.rangeChart
            if (!ref) return null
            if (Array.isArray(ref) && ref.length > 0) return ref[0]
            return ref
        },

        resizeApexCharts() {
            ['trendChart', 'chart', 'weeklyChart'].forEach(refName => {
                const chart = this.getApexInstance(refName)
                if (chart && typeof chart.windowResizeHandler === 'function') {
                    chart.windowResizeHandler()
                    return
                }
                if (chart && typeof chart._windowResize === 'function') {
                    chart._windowResize()
                    return
                }

                const component = this.getApexComponent(refName)
                if (component && typeof component.refresh === 'function') {
                    component.refresh()
                }
            })
        },

        resizeRangeChart() {
            const rangeChart = this.getRangeChartComponent()
            if (!rangeChart) return
            if (typeof rangeChart.scheduleChartResize === 'function') {
                rangeChart.scheduleChartResize()
                return
            }
            if (typeof rangeChart.resizeChart === 'function') {
                rangeChart.resizeChart()
            }
        },

        resizeAllCharts() {
            this.resizeApexCharts()
            this.resizeRangeChart()
        },

        clearScheduledChartResize() {
            this.layoutResizeTimers.forEach(timerId => clearTimeout(timerId))
            this.layoutResizeTimers = []
        },

        scheduleChartResize() {
            this.clearScheduledChartResize()
            this.$nextTick(() => {
                this.resizeAllCharts()

                ;[320, 700].forEach(delay => {
                    const timerId = setTimeout(() => {
                        this.resizeAllCharts()
                    }, delay)
                    this.layoutResizeTimers.push(timerId)
                })
            })
        },

        handleWindowResize() {
            this.scheduleChartResize()
        },

        async getDevicesData() {
            console.log(`[SimpleChars] getDevicesData() called, deviceId: ${this.deviceId}, devices: ${this.devices}`)
            let dataloca = []
            const currentDay = moment().format("YYYY-MM-DD")

            if ( this.devices ) {
                this.loadingDailyChart = false
                console.log(`[SimpleChars] Mode: comparison, devices count: ${this.devices.length}`)
                for (let i = 0; i < this.devices.length; i++) {
                    const device = this.devices[i]

                    if ( device in this.dashBoardDevices ) {
                        dataloca.push({
                            deviceId: device,
                            info: this.dashBoardDevices[device]
                        })
                    }
                }
            } else {
                this.loadingDailyChart = true
                try {
                    console.log(`[SimpleChars] Mode: single device, deviceId: ${this.deviceId}`)
                    const url = `${settings.URL_API}/report/${this.deviceId}/laeq_001?day=${currentDay}`
                    console.log(`[SimpleChars] Fetching from API: ${url}`)
                    const response = await axios.get(url, { headers: authHeader() })
                    console.log(`[SimpleChars] API response received, data length: ${response.data ? response.data.length : 0}`)
                    const data = {
                        deviceId: this.deviceId,
                        info: response.data
                    }

                    this.$store.dispatch('saveDashboardData', data)
                    dataloca.push(data)

                    // Solo en la primera carga (sin datos previos)
                    // Las reactivaciones de slide usan sus propios timers
                    if (this.dailyTrends.length === 0) {
                        this.fetchDailyTrends(this.deviceId)
                    }
                    if (this.rangeStats === null) {
                        this.fetchRangeStats(this.deviceId)
                    }
                    this.fetchWeeklySeries(this.deviceId)
                } catch (error) {
                    this.loadingDailyChart = false
                    throw error
                }
            }

            console.log(`[SimpleChars] Calling dataCharts2 with ${dataloca.length} items`)
            this.dataCharts2(dataloca)
        },

        async fetchDailyTrends(deviceId) {
            this.loadingHourly = true
            this.dailyTrends = []
            // Find sensor name from store or default
            let sensorName = 'laeq_001'
            const devicesInfo = this.findDeviceInfo(deviceId)
            if (devicesInfo && devicesInfo.sensors) {
                 const laeqSensor = devicesInfo.sensors.find(s => s.sensorType === 'laeq')
                 if (laeqSensor) sensorName = laeqSensor.sensorName
            }

            const trends = await this.$store.dispatch('getDeviceDailyTrends', {
                deviceId: deviceId,
                sensorName: sensorName
            })

            if (trends && Array.isArray(trends)) {
                // Sort by time
                this.dailyTrends = trends.sort((a, b) => new Date(a.ts) - new Date(b.ts))
                this.updateTrendChart()
            }
            this.loadingHourly = false
        },

        async fetchRangeStats(deviceId) {
            this.loadingRange = true
            this.rangeStats = null
             let sensorName = 'laeq_001'
            const devicesInfo = this.findDeviceInfo(deviceId)
            if (devicesInfo && devicesInfo.sensors) {
                 const laeqSensor = devicesInfo.sensors.find(s => s.sensorType === 'laeq')
                 if (laeqSensor) sensorName = laeqSensor.sensorName
            }
            
            const endDate = moment().format('YYYY-MM-DD')
            const startDate = moment().subtract(6, 'days').format('YYYY-MM-DD') // Last 7 days including today

            try {
                const stats = await this.$store.dispatch('getDeviceRangeStats', {
                    deviceId: deviceId,
                    sensorName: sensorName,
                    startDate: startDate,
                    endDate: endDate
                })
                this.rangeStats = stats
            } catch (err) {
                console.error(`[SimpleChars] Error fetching stats for ${deviceId}:`, err)
            } finally {
                this.loadingRange = false
            }
        },

        updateTrendChart() {
            const categories = this.dailyTrends.map(t => moment(t.ts).format('HH'))
            const data = this.dailyTrends.map(t => t.moda)
            const valid = data.filter(v => v !== null && v !== undefined && !Number.isNaN(v))
            let yaxis = { ...this.trendOptions.yaxis }
            if (valid.length > 0) {
                const min = Math.min(...valid)
                const max = Math.max(...valid)
                const pad = Math.max(1, (max - min) * 0.05)
                yaxis = {
                    ...yaxis,
                    min: min - pad,
                    max: max + pad,
                    tickAmount: 3
                }
            } else {
                yaxis = {
                    ...yaxis,
                    min: undefined,
                    max: undefined,
                    tickAmount: 3
                }
            }

            this.trendSeries = [{
                name: 'Tendencia',
                data: data
            }]
            
            this.trendOptions = {
                ...this.trendOptions,
                xaxis: {
                    categories: categories,
                     labels: {
                        style: {
                            fontSize: '10px'
                        }
                    }
                },
                yaxis
            }
        },

        formatTime(ts) {
            return moment(ts).format('HH:mm')
        },

        formatTimeRange(ts) {
            if (!ts) return ''
            const startTime = moment(ts).startOf('hour')
            const endTime = moment(ts)
            return `${startTime.format('HH:mm')}-${endTime.format('HH:mm')}`
        },

        formatHourLabel(ts) {
            if (!ts) return ''
            return `${moment(ts).format('HH')} hrs`
        },

        getLaeqSensorName(deviceId) {
            const devicesInfo = this.findDeviceInfo(deviceId)
            if (devicesInfo && devicesInfo.sensors) {
                const laeqSensor = devicesInfo.sensors.find(s => s.sensorType === 'laeq')
                if (laeqSensor) return laeqSensor.sensorName
            }
            return 'laeq_001'
        },

        getLaeqColor(laeq) {
            if (laeq === null || laeq === undefined) return '#9E9E9E'
            if (laeq < 35) return '#A0BABF'
            if (laeq < 40) return '#A0BABF'
            if (laeq < 45) return '#B8D6D1'
            if (laeq < 50) return '#CEE4CC'
            if (laeq < 55) return '#E2F2BF'
            if (laeq < 60) return '#F3C683'
            if (laeq < 65) return '#E87E4D'
            if (laeq < 70) return '#CD463E'
            if (laeq < 75) return '#A11A4D'
            if (laeq < 80) return '#75085C'
            return '#430A4A'
        },

        buildWeeklySeries(records, deviceId, sensorName) {
            if (!records || records.length === 0) return []

            const deviceInfo = this.findDeviceInfo(deviceId)
            let laeqFilter = null
            if (deviceInfo && deviceInfo.laeqFilter) {
                laeqFilter = deviceInfo.laeqFilter
            }

            const offset = this.laeqOffset(deviceId, sensorName)
            const data = []

            for (let i = 0; i < records.length; i += 1) {
                const rec = records[i]
                if (!rec || !rec.ts || !rec.report || rec.report.avg === undefined || rec.report.avg === null) continue
                let value = rec.report.avg + offset
                if (laeqFilter) {
                    if (value > laeqFilter.max || value < laeqFilter.min) value = null
                }
                data.push({
                    x: new Date(rec.ts).getTime(),
                    y: value
                })
            }

            data.sort((a, b) => a.x - b.x)

            return [
                {
                    type: 'line',
                    name: 'LAeq 7 días',
                    data: data
                }
            ]
        },

        getReferenceLaeqTime() {
            if (this.headerDeviceInfo && this.headerDeviceInfo.ts) {
                const ts = moment(this.headerDeviceInfo.ts)
                if (ts.isValid()) return ts
            }

            if (this.currentLaeqValue !== null && this.currentLaeqValue !== undefined) {
                const last = this.getLastChartTimestamp()
                if (last) return last
            }

            return moment()
        },

        getLastChartTimestamp() {
            if (!this.series || this.series.length === 0) return null
            const firstSeries = this.series[0]
            if (!firstSeries || !firstSeries.data || firstSeries.data.length === 0) return null
            const lastPoint = firstSeries.data[firstSeries.data.length - 1]
            if (!lastPoint || !lastPoint.x) return null
            return moment(lastPoint.x)
        },

        getOperativityValue(item) {
            if (!item) return null
            const rawValue =
                item.porcentajeConData !== undefined && item.porcentajeConData !== null ? item.porcentajeConData
                    : item.operatividad !== undefined && item.operatividad !== null ? item.operatividad
                        : item.operativity !== undefined && item.operativity !== null ? item.operativity
                            : item.operativityPercent !== undefined && item.operativityPercent !== null ? item.operativityPercent
                                : null

            if (rawValue === null || rawValue === undefined) return null

            const numericValue = Number(rawValue)
            if (!Number.isFinite(numericValue)) return null

            return Math.min(Math.max(numericValue, 0), 100)
        },

        formatOperativityRangeLabel(startHour, endHour) {
            return `${padHour(startHour)}:00-${padHour(endHour)}:59`
        },

        async fetchWeeklySeries(deviceId) {
            if (!deviceId || this.loadingWeeklySeries) return

            this.loadingWeeklySeries = true
            const sensorName = this.getLaeqSensorName(deviceId)
            const days = []
            for (let i = 0; i < 7; i += 1) {
                days.push(moment().subtract(i, 'days').format('YYYY-MM-DD'))
            }

            const requests = days.map(day => {
                const url = `${settings.URL_API}/report/${deviceId}/${sensorName}?day=${day}`
                return axios.get(url, { headers: authHeader() }).catch(() => null)
            })

            try {
                const responses = await Promise.all(requests)
                const records = responses
                    .filter(r => r && r.data && Array.isArray(r.data))
                    .reduce((acc, r) => acc.concat(r.data), [])

                this.weeklySeries = this.buildWeeklySeries(records, deviceId, sensorName)
            } catch (error) {
                this.weeklySeries = []
            } finally {
                this.loadingWeeklySeries = false
            }
        },

        dataCharts2(data) {
            console.log(`[SimpleChars] dataCharts2() called with ${data.length} items`)

            this.series = []
            let latestValue = null

            for (let j = 0; j < data.length; j++) {
                let Laeqfs = []
                const devicesInfo = this.findDeviceInfo(data[j].deviceId)
                console.log(`[SimpleChars] dataCharts2 - deviceId: ${data[j].deviceId}, devicesInfo: ${devicesInfo ? 'found' : 'NOT FOUND'}`)

                if (!devicesInfo) {
                    console.log(`[SimpleChars] dataCharts2 - RETURNING EARLY because devicesInfo is null for ${data[j].deviceId}`)
                    return
                }

                this.deviceName = devicesInfo.name

                const laeqSensor = devicesInfo.sensors.find(e => {
                    return e.sensorType === 'laeq'
                }).sensorName

                let offsetloco = this.laeqOffset(data[j].deviceId, laeqSensor)
                const info = data[j].info

                if (info.length === 0)
                    continue

                for (let i = 0; i < info.length; i++) {
                    const laqeValue = info[i].report.avg + offsetloco
                    // Unused variables logic removed for clarity
                    
                    this.minLaeqfs = laqeValue < this.minLaeqfs ? laqeValue : this.minLaeqfs
                    let laeqSensorValue
                    let laeqFilter

                    if (!Object.hasOwnProperty.call(devicesInfo, "laeqFilter")) {
                        laeqFilter = {
                            max: 100,
                            min: 0
                        }
                    } else {
                        laeqFilter = devicesInfo.laeqFilter
                    }

                    if (laqeValue > laeqFilter.max || laqeValue < laeqFilter.min)
                        laeqSensorValue = null
                    else
                        laeqSensorValue = laqeValue

                    if (laeqSensorValue !== null && laeqSensorValue !== undefined) {
                        latestValue = laeqSensorValue
                    }

                    Laeqfs.push({
                        str: info[i].ts,
                        x: new Date(info[i].ts).getTime(),
                        y: laeqSensorValue
                    })
                }
                
                this.series.push({
                    type: 'line',
                    name: devicesInfo.name,
                    data: Laeqfs,
                    color: devicesInfo.info.color,
                    _p: devicesInfo.info.displayRank
                })
                
                if (data.length === 1) {
                    let dayReport = moment(info[0].ts).format("YYYY-MM-DD")
                    this.loadComplianceLimitsChart(data[j].deviceId, laeqSensor, dayReport)
                }
            }

            this.series.sort((a, b) => b._p - a._p)
            if (data.length === 1) {
                this.currentLaeqValue = latestValue
            }
            this.loadingDailyChart = false
            this.scheduleChartResize()
        },
        
        loadComplianceLimitsChart (deviceId, sensorName, dayReport) {
            let limits = this.getComplianceLimits(deviceId, sensorName)

            if (limits === null || limits.red === null || limits.yellow === null)
                return

            if (!limits.enabled)
                return
            
          
            const startTime = moment(`${dayReport} 00:00:00`).format()
            const endTime = moment(`${dayReport} 23:59:59`).format()

            this.series.push({
                type: 'line',
                name: "Umbral alerta roja",
                color: '#E53935',
                data: [
                    { x: startTime, y: limits.red },
                    { x: endTime, y: limits.red }
                ]
            })

            this.series.push({
                type: 'line',
                name: "Umbral alerta amarilla",
                color: '#FDD835',
                data: [
                    { x: startTime, y: limits.yellow },
                    { x: endTime, y: limits.yellow }
                ]
            })
        },

        laeqOffset(deviceId, sensorName) {
            const findSensor = this.allSensors.filter(sensor => sensor.deviceId === deviceId && sensor.sensorName === sensorName)

            if (findSensor.length > 0)
                return findSensor[0].offset

            return 0
        },

        getComplianceLimits (deviceId, sensorName) {
            const findSensor = this.allSensors.filter( sensor => sensor.deviceId === deviceId && sensor.sensorName === sensorName )

            if (findSensor.length > 0)
                return findSensor[0].complianceLimits

            return null
        },

        findDeviceInfo(deviceId) {
            return this.$store.getters.device(deviceId)
        },

        loadData() {
            console.log(`[SimpleChars] loadData() called, deviceId: ${this.deviceId}`)
            this.$nextTick(() => {
                console.log(`[SimpleChars] loadData() -> $nextTick -> getDevicesData()`)
                this.getDevicesData()
                this.scheduleChartResize()
            })
        },

        // Refresca únicamente el LAeq del día actual (sin re-cargar tendencias ni promedios)
        async fetchDailyLaeq() {
            const currentDay = moment().format('YYYY-MM-DD')
            let dataloca = []
            try {
                if (this.devices) {
                    for (let i = 0; i < this.devices.length; i++) {
                        const device = this.devices[i]
                        if (device in this.dashBoardDevices) {
                            dataloca.push({ deviceId: device, info: this.dashBoardDevices[device] })
                        }
                    }
                } else {
                    this.loadingDailyChart = true
                    const url = `${settings.URL_API}/report/${this.deviceId}/laeq_001?day=${currentDay}`
                    const response = await axios.get(url, { headers: authHeader() })
                    const data = { deviceId: this.deviceId, info: response.data }
                    this.$store.dispatch('saveDashboardData', data)
                    dataloca.push(data)
                }
                this.dataCharts2(dataloca)
            } catch (e) {
                this.loadingDailyChart = false
                console.error('[SimpleChars] fetchDailyLaeq error:', e)
            }
        },

        // Milisegundos hasta el próximo :00 de hora
        msUntilNextHour() {
            return moment().startOf('hour').add(1, 'hour').diff(moment())
        },

        // Milisegundos hasta el próximo disparo a las 07:00 o 21:00
        msUntilNextRangeTrigger() {
            const now = moment()
            const candidates = [7, 21].flatMap(h => [
                moment().startOf('day').add(h, 'hours'),
                moment().startOf('day').add(1, 'day').add(h, 'hours')
            ]).filter(t => t.isAfter(now))
            candidates.sort((a, b) => a.diff(b))
            return candidates[0].diff(now)
        },

        // Timer: refresca LAeq día actual cada 1 minuto
        startLaeqRefresh() {
            if (this.laeqRefreshTimer) clearInterval(this.laeqRefreshTimer)
            this.laeqRefreshTimer = setInterval(() => {
                if (this.isActive) this.fetchDailyLaeq()
            }, 60 * 1000)
        },

        // Timer: refresca tendencias por hora al :00 de cada hora
        startHourlyTrendRefresh() {
            if (this.hourlyTrendTimer) clearTimeout(this.hourlyTrendTimer)
            const delay = this.msUntilNextHour()
            this.hourlyTrendTimer = setTimeout(() => {
                if (this.isActive && this.isDeviceChart && this.deviceId) {
                    this.fetchDailyTrends(this.deviceId)
                }
                this.startHourlyTrendRefresh()
            }, delay)
        },

        // Timer: refresca rango horario (últimos 7 días) a las 07:00 y 21:00
        startRangeStatsRefresh() {
            if (this.rangeStatsTimer) clearTimeout(this.rangeStatsTimer)
            const delay = this.msUntilNextRangeTrigger()
            this.rangeStatsTimer = setTimeout(() => {
                if (this.isActive && this.isDeviceChart && this.deviceId) {
                    this.fetchRangeStats(this.deviceId)
                }
                this.startRangeStatsRefresh()
            }, delay)
        },

        // Timer: refresca el gráfico LAeq últimos 7 días cada 1 hora
        startWeeklyRefresh() {
            if (this.weeklyRefreshTimer) clearInterval(this.weeklyRefreshTimer)
            this.weeklyRefreshTimer = setInterval(() => {
                if (this.isActive && this.isDeviceChart && this.deviceId) {
                    this.fetchWeeklySeries(this.deviceId)
                }
            }, 60 * 60 * 1000)
        },

        stopAllTimers() {
            if (this.laeqRefreshTimer) { clearInterval(this.laeqRefreshTimer); this.laeqRefreshTimer = null }
            if (this.hourlyTrendTimer) { clearTimeout(this.hourlyTrendTimer); this.hourlyTrendTimer = null }
            if (this.rangeStatsTimer) { clearTimeout(this.rangeStatsTimer); this.rangeStatsTimer = null }
            if (this.weeklyRefreshTimer) { clearInterval(this.weeklyRefreshTimer); this.weeklyRefreshTimer = null }
        },

        compareValue(current, reference) {
            if (reference === null || reference === undefined || Number.isNaN(reference)) return 'N/A'
            if (current > reference) return 'sobre'
            if (current < reference) return 'bajo'
            return 'igual'
        },
    },

    computed: {
        isLoading() {
            return this.$store.getters.isLoading
        },

        hasDevices () {
            return this.devices ? this.devices.length : 0
        },

        devicesInfo () {
            return this.$store.getters.devicesInfo
        },

        deviceLastReport () {
            return this.devicesInfo[this.deviceId]
        },

        allSensors () {
            return this.$store.getters.allSensors
        },

        dashBoardDevices () {
            return this.$store.getters.dashBoardDevices
        },

        isDeviceChart () {
            return !this.devices || this.devices.length === 0
        },

        headerDeviceInfo() {
            return this.$store.getters.deviceInfo(this.deviceId)
        },

        headerLaeqValue() {
            if (!this.headerDeviceInfo || !this.headerDeviceInfo.sensors) return '--'
            const laeqSensor = this.headerDeviceInfo.sensors.find(s => s.sensorType === 'laeq')
            if (!laeqSensor || laeqSensor.value === null || laeqSensor.value === undefined) return '--'
            return laeqSensor.value.toFixed(1)
        },

        headerLaeqNumber() {
            if (!this.headerDeviceInfo || !this.headerDeviceInfo.sensors) return null
            const laeqSensor = this.headerDeviceInfo.sensors.find(s => s.sensorType === 'laeq')
            if (!laeqSensor || laeqSensor.value === null || laeqSensor.value === undefined) return null
            return laeqSensor.value
        },

        headerLaeqColor() {
            return this.getLaeqColor(this.headerLaeqNumber)
        },

        laeqCurrentValue() {
            if (this.headerLaeqNumber !== null && this.headerLaeqNumber !== undefined) return this.headerLaeqNumber
            if (this.currentLaeqValue !== null && this.currentLaeqValue !== undefined) return this.currentLaeqValue
            return null
        },

        weeklyAverages() {
            if (!this.isDeviceChart || !this.deviceId) return null
            return this.$store.getters.dashboardWeeklyAverage(this.deviceId)
        },

        weeklyLoading() {
            if (!this.isDeviceChart || !this.deviceId) return false
            const status = this.$store.getters.dashboardWeeklyAverageStatus(this.deviceId)
            return status === 'idle' || status === 'loading'
        },

        laeqArrowSymbol() {
            if (this.weeklyLoading || !this.weeklyAverages || this.weeklyAverages.value === null) return ''
            if (this.laeqCurrentValue === null || this.laeqCurrentValue === undefined) return ''
            if (this.laeqCurrentValue < this.weeklyAverages.value) return 'down'
            if (this.laeqCurrentValue > this.weeklyAverages.value) return 'up'
            return 'equal'
        },

        laeqArrowClass() {
            if (!this.laeqArrowSymbol) return ''
            if (this.laeqArrowSymbol === 'down') return 'laeq-arrow--down'
            if (this.laeqArrowSymbol === 'up') return 'laeq-arrow--up'
            return 'laeq-arrow--equal'
        },

        laeqArrowIcon() {
            if (!this.laeqArrowSymbol) return ''
            if (this.laeqArrowSymbol === 'down') return 'mdi-arrow-down'
            if (this.laeqArrowSymbol === 'up') return 'mdi-arrow-up'
            return 'mdi-arrow-right'
        },

        laeqDeltaDisplay() {
            if (this.weeklyLoading || !this.weeklyAverages || this.weeklyAverages.value === null) return ''
            if (this.laeqCurrentValue === null || this.laeqCurrentValue === undefined) return ''
            const delta = this.laeqCurrentValue - this.weeklyAverages.value
            const sign = delta >= 0 ? '+' : '-'
            return `${sign}${Math.abs(delta).toFixed(1)}`
        },

        showWeeklyComparisonDelta() {
            return !this.weeklyLoading && this.weeklyMinuteNumber !== null && this.headerLaeqValue !== '--' && !!this.laeqArrowIcon
        },

        headerLaeqTime() {
            if (!this.headerDeviceInfo || !this.headerDeviceInfo.ts) return '--'
            return this.formatTime(this.headerDeviceInfo.ts)
        },

        laeqReferenceTime() {
            const ref = this.getReferenceLaeqTime()
            return ref ? this.formatTime(ref) : '--'
        },

        headerTrendData() {
            if (!this.dailyTrends || this.dailyTrends.length === 0) return null
            const currentHour = moment().hour()
            const exact = this.dailyTrends.find(item => moment(item.ts).hour() === currentHour)
            return exact || this.dailyTrends[this.dailyTrends.length - 1]
        },

        headerTrendValue() {
            if (!this.headerTrendData || this.headerTrendData.moda === null || this.headerTrendData.moda === undefined) return '--'
            return this.headerTrendData.moda.toFixed(0)
        },

        headerTrendNumber() {
            if (!this.headerTrendData || this.headerTrendData.moda === null || this.headerTrendData.moda === undefined) return null
            return this.headerTrendData.moda
        },

        headerTrendColor() {
            return this.getLaeqColor(this.headerTrendNumber)
        },

        headerTrendTime() {
            if (!this.headerTrendData || !this.headerTrendData.ts) return '--'
            return this.formatHourLabel(this.headerTrendData.ts)
        },

        headerOperativity() {
            if (!this.operativityDetails) return '--'
            if (!this.operativityDetails.hasClosedHours) return '--'
            return `${Math.round(this.operativityDetails.average)}%`
        },

        operativityCaption() {
            return 'Día actual'
        },

        weeklyMinuteNumber() {
            if (!this.weeklyAverages || this.weeklyAverages.value === null || this.weeklyAverages.value === undefined) return null
            return this.weeklyAverages.value
        },

        headerAverageColor() {
            return this.getLaeqColor(this.weeklyMinuteNumber)
        },

        averageTooltipText() {
            if (!this.weeklyHourMinuteLabel) {
                return 'Se calcula tomando los valores LAeq de la misma hora:minuto en cada uno de los últimos 7 días, y promediándolos en un único valor.'
            }
            return `Se calcula tomando todos los valores LAeq medidos a las ${this.weeklyHourMinuteLabel} de cada uno de los últimos 7 días, y promediándolos en un único valor para esa hora:minuto.`
        },

        weeklyMinuteDisplay() {
            if (this.weeklyLoading) return '--'
            if (!this.weeklyAverages || this.weeklyAverages.value === null) return '--'
            return `${this.weeklyAverages.value.toFixed(1)}`
        },

        weeklyHourMinuteLabel() {
            if (!this.weeklyAverages) return ''
            return this.weeklyAverages.hourMinute || ''
        },

        operativityDetails() {
            const endHour = moment().hour() - 1
            if (endHour < 0) {
                return {
                    hasClosedHours: false,
                    startHour: 0,
                    endHour: -1,
                    hoursInRange: 0,
                    missingHours: [],
                    average: null,
                    rangeLabel: ''
                }
            }

            const valuesByHour = (this.dailyTrends || []).reduce((acc, item) => {
                if (!item || !item.ts) return acc

                const hour = moment(item.ts).hour()
                if (!Number.isFinite(hour) || hour < 0 || hour > endHour) return acc

                const value = this.getOperativityValue(item)
                if (value === null || value === undefined || Number.isNaN(value)) return acc

                acc[hour] = Number(value)
                return acc
            }, {})

            const operativityByHour = []
            const missingHours = []
            let sum = 0

            for (let h = 0; h <= endHour; h += 1) {
                const hasHourValue = Object.prototype.hasOwnProperty.call(valuesByHour, h)
                const hourValue = hasHourValue ? Number(valuesByHour[h]) : 0

                if (!hasHourValue) {
                    missingHours.push(h)
                }

                sum += hourValue
                operativityByHour.push({
                    hour: h,
                    valueRaw: hourValue,
                    valueNormalized: hourValue
                })
            }

            const hoursInRange = endHour + 1
            const average = hoursInRange > 0 ? (sum / hoursInRange) : null

            return {
                hasClosedHours: true,
                startHour: 0,
                endHour,
                hoursInRange,
                missingHours,
                average,
                rangeLabel: this.formatOperativityRangeLabel(0, endHour),
                operativityByHour,
                valuesByHour
            }
        },


        isDeviceReady() {
            if (this.devices && this.devices.length > 0) {
                // Modo comparación: verificar que al menos un dispositivo esté listo
                return this.devices.some(deviceId => {
                    const info = this.$store.getters.device(deviceId)
                    return info !== undefined && info !== null
                })
            } else if (this.deviceId) {
                // Modo dispositivo individual
                const info = this.$store.getters.device(this.deviceId)
                return info !== undefined && info !== null
            }
            return false
        },

        storeDevices() {
            return this.$store.getters.devices
        },

        drawer() {
            return this.$store.getters.drawer
        }
    },

    watch: {
        // Al cambiar de dispositivo (cambio de proyecto) limpiar estado para forzar recarga fresca
        deviceId(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.dailyTrends = []
                this.rangeStats = null
                this.weeklySeries = []
                this.loadingWeeklySeries = false
                this.loadingDailyChart = false
            }
        },

        isActive: {
            handler(val) {
                console.log(`[SimpleChars] isActive changed: ${val}, deviceId: ${this.deviceId}`)
                if (val) {
                    console.log(`[SimpleChars] isActive=true -> calling loadData()`)
                    this.loadData()
                    this.startLaeqRefresh()
                    this.startHourlyTrendRefresh()
                    this.startRangeStatsRefresh()
                    this.startWeeklyRefresh()
                    this.scheduleChartResize()
                } else {
                    this.stopAllTimers()
                }
            },
            immediate: true
        },

        isDeviceReady: {
            handler(ready) {
                console.log(`[SimpleChars] isDeviceReady changed: ${ready}, deviceId: ${this.deviceId}, isActive: ${this.isActive}`)
                if (ready && this.isActive) {
                     // Si ya se cargó por isActive, esto podría ser redundante pero seguro
                    console.log(`[SimpleChars] isDeviceReady=true AND isActive=true -> ensuring data load`)
                    this.loadData()
                    this.scheduleChartResize()
                }
            },
            immediate: true
        },
        storeDevices: {
            handler(newDevices) {
                console.log(`[SimpleChars] storeDevices changed, count: ${newDevices ? newDevices.length : 0}, isActive: ${this.isActive}, isDeviceReady: ${this.isDeviceReady}`)
                if (this.isActive && this.isDeviceReady) {
                    console.log(`[SimpleChars] storeDevices changed -> calling loadData()`)
                    this.loadData()
                }
            },
            deep: true
        },
        drawer() {
            if (!this.isActive) return
            this.scheduleChartResize()
        }
    },

    filters: {
        dateFormat (val) {
            if ( !val )
                return "Sin Datos"

            if (!moment(val).isValid())
                return val

            return moment(val).format('LLL')
        },
    },

    components: {
        apexchart: VueApexCharts,
        DashboardRangeTrendChart
    },

    created() {
        moment.locale('es-us')
    },

    mounted() {
        window.addEventListener('resize', this.handleWindowResize, { passive: true })
        window.addEventListener('orientationchange', this.handleWindowResize, { passive: true })
        this.scheduleChartResize()
    },

    beforeDestroy() {
        this.stopAllTimers()
        window.removeEventListener('resize', this.handleWindowResize)
        window.removeEventListener('orientationchange', this.handleWindowResize)
        this.clearScheduledChartResize()
    }
}
</script>
<style scoped>
.top-summary {
    margin-bottom: 2px;
}
.summary-row {
    flex-wrap: wrap;
    overflow-x: hidden;
}
.summary-skeleton-card {
    min-height: 86px;
}
.summary-skeleton-block {
    display: flex;
    align-items: center;
    min-height: 64px;
}
.simple-chars-container {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    height: 100%;
    min-height: 0;
    overflow: hidden;
}
.device-chart-row {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    width: 100%;
}
.comparison-chart-row {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    width: 100%;
}
.comparison-chart-header {
    flex: 0 0 auto;
    width: 100%;
    padding-bottom: 8px;
}
.comparison-chart-loading {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    width: 100%;
}
.comparison-skeleton-fill {
    flex: 1 1 0;
}
.comparison-chart-shell {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    display: flex;
}
.comparison-chart-card {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    display: flex;
}
.comparison-chart-card > * {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    width: 100%;
}
.summary-slot {
    flex: 0 0 auto;
    width: 100%;
}
.charts-slot {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
}
.charts-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 10px;
    min-height: 0;
    height: 100%;
    width: 100%;
}
.chart-column {
    min-height: 0;
    display: grid;
    grid-template-rows: minmax(0, 0.92fr) minmax(0, 1.08fr);
    gap: 8px;
    height: 100%;
    padding: 0;
}
.device-name {
    font-size: 2.65rem;
    line-height: 1.05;
    font-weight: 800;
}
.summary-block {
    padding-right: 10px;
    min-width: 120px;
    flex: 0 1 auto;
}
.device-block {
    margin-right: 16px;
    flex: 0 0 30%;
    max-width: 30%;
}
.laeq-pack {
    margin-left: 12px;
}
.laeq-pack__row {
    flex-wrap: nowrap;
}
.laeq-pack__item {
    padding-right: 8px;
}
.laeq-pack__delta {
    min-width: 310px;
    min-height: 60px;
    display: flex;
    align-items: center;
}
.laeq-pack__compare {
    min-width: 220px;
}
.right-pack {
    padding-right: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 60px;
}
.right-pack__inner {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 56px;
}
.right-pack-loading {
    min-width: 150px;
    min-height: 56px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    row-gap: 2px;
}
.right-pack__item {
    min-width: 96px;
}
.chart-card {
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
}
.chart-title {
    padding-bottom: 1px !important;
    margin-bottom: 0 !important;
    line-height: 1.1;
    padding-top: 1px !important;
    min-height: 0 !important;
    height: auto !important;
    color: #000000 !important;
}
.chart-card--top .chart-title {
    padding-top: 3px !important;
}
.chart-card .v-card__text {
    padding-top: 0 !important;
    margin-top: 0 !important;
}
.range-chart {
    margin-top: 0;
    min-height: 0;
    display: flex;
    height: 100%;
}
.range-chart > * {
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
}
.chart-body {
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding-top: 0 !important;
}
.laeq-chart-body {
    height: auto;
}
.laeq-weekly-chart-body {
    margin-top: 0 !important;
}
.top-chart-body {
    margin-top: 0 !important;
}
.chart-loading-shell {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
}
.chart-skeleton-fill {
    flex: 1 1 auto;
    height: 100% !important;
}
.chart-skeleton-fill >>> .v-skeleton-loader__image {
    height: 100% !important;
    margin: 0 !important;
}
.summary-skeleton-line {
    margin-bottom: 0 !important;
}
.summary-skeleton-line >>> .v-skeleton-loader__text {
    margin: 0 !important;
}
.ref-header {
    margin-bottom: 4px;
}
.ref-subtext {
    color: #9e9e9e;
    font-size: 0.7rem;
}
.metric-value {
    font-weight: 600;
    font-size: 1rem;
    display: inline-block;
}
.metric-primary {
    font-size: 2.75rem;
    line-height: 0.85;
}
.summary-card .v-card__title {
    padding-bottom: 2px !important;
}
.summary-card .v-card__text {
    padding-top: 0 !important;
    padding-bottom: 4px !important;
}
.metric-row .metric-title {
    margin-bottom: 2px;
}
.metric-row .metric-value {
    line-height: 1.1;
}
.metric-row .caption {
    margin-top: 2px;
}
.metric-title {
    font-size: 0.7rem;
    font-weight: 600;
    color: #616161;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}
.value-underline {
    display: inline-block;
    padding-bottom: 1px;
    border-bottom: 4px solid rgba(0, 0, 0, 0.1);
    opacity: 0.85;
}
.laeq-title {
    font-size: 0.6rem;
    color: #9e9e9e;
    text-transform: none;
    letter-spacing: 0.2px;
    margin-bottom: 2px;
}
.laeq-value {
    margin-top: -2px;
}
.laeq-time {
    text-align: left;
}
.laeq-arrow {
    font-size: 1.1rem;
    font-weight: 700;
    margin-left: 0;
    vertical-align: middle;
}
.laeq-arrow--value {
    font-size: 1.25rem;
}
.laeq-arrow-icon {
    font-size: 1.1rem !important;
    margin-right: 2px;
}
.laeq-delta-unit {
    font-size: 0.7rem;
    color: #9e9e9e;
    margin-left: 2px;
    font-weight: 500;
}
.avg-pack-title {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.avg-delta-split {
    display: flex;
    align-items: stretch;
    justify-content: center;
}
.avg-summary-loading {
    min-width: 170px;
    min-height: 60px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    row-gap: 2px;
}
.avg-delta-col {
    display: flex;
    flex-direction: column;
    justify-content: center;
}
.avg-delta-split {
    min-height: 60px;
}
.avg-delta-col--delta {
    min-width: 130px;
    align-items: flex-end;
    text-align: right;
}
.avg-delta-col--delta .laeq-arrow {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
}
.avg-delta-col--avg {
    min-width: 150px;
    align-items: flex-start;
}
.avg-delta-divider {
    margin: 0 8px !important;
}
.avg-info-inline {
    cursor: help;
    color: #5f9f78;
    font-size: 0.8rem !important;
    font-weight: 600;
    line-height: 1;
    vertical-align: middle;
    opacity: 0.85;
}
.avg-info-inline:hover {
    opacity: 1;
}
.avg-inline {
    margin-top: -2px;
}
.avg-value {
    font-size: 2.1rem !important;
}
.delta-note {
    margin-top: 2px;
    text-align: right;
    line-height: 1.1;
    width: 100%;
}
.laeq-inline {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    white-space: nowrap;
}
.laeq-unit {
    font-size: 0.65rem;
    color: #9e9e9e;
    margin-left: 2px;
}
.metric-gap {
    padding-right: 18px !important;
}
.trend-gap {
    padding-left: 10px !important;
}
.laeq-arrow--down {
    color: #2e7d32;
}
.laeq-arrow--up {
    color: #c62828;
}
.laeq-arrow--equal {
    color: #9e9e9e;
}
.laeq-compare {
    font-size: 0.62rem;
    line-height: 1.1;
    margin-top: 1px;
}
.laeq-meta {
    display: flex;
    justify-content: space-between;
    font-size: 0.7rem;
    margin-top: 1px;
    color: #6d6d6d;
}
.laeq-meta-left {
    justify-content: flex-start;
}
.laeq-meta__time {
    text-align: left;
}
.laeq-meta__range {
    text-align: right;
    margin-left: 0;
}
@media (max-width: 1264px) {
    .device-block {
        flex: 0 0 34%;
        max-width: 34%;
    }

    .laeq-pack__delta {
        min-width: 270px;
    }
}

@media (max-width: 960px) {
    .simple-chars-container {
        overflow-y: auto;
    }

    .chart-column {
        display: flex;
        gap: 10px;
        height: auto;
    }

    .comparison-chart-row {
        height: auto;
    }

    .charts-row {
        grid-template-columns: minmax(0, 1fr);
        row-gap: 10px;
        height: auto;
    }

    .chart-body {
        height: 155px;
    }

    .laeq-chart-body {
        height: 130px;
    }

    .summary-row {
        gap: 6px;
    }

    .summary-block {
        padding-right: 4px;
    }

    .device-block {
        flex: 0 0 100%;
        max-width: 100%;
        margin-right: 0;
        margin-bottom: 4px;
    }

    .device-name {
        font-size: 2.1rem !important;
    }

    .laeq-pack {
        margin-left: 0;
        width: 100%;
    }

    .laeq-pack__row {
        flex-wrap: wrap;
        row-gap: 6px;
    }

    .laeq-pack__delta {
        min-width: 0;
        width: 100%;
    }

    .avg-delta-split {
        justify-content: flex-start;
        flex-wrap: wrap;
        gap: 6px;
    }

    .avg-delta-divider {
        display: none;
    }

    .avg-delta-col--delta {
        align-items: flex-start;
        text-align: left;
        min-width: 0;
    }

    .avg-delta-col--delta .laeq-arrow {
        justify-content: flex-start;
    }

    .avg-delta-col--avg {
        min-width: 0;
    }

    .delta-note {
        text-align: left;
    }

    .right-pack {
        width: 100%;
        margin-top: 2px;
        align-items: flex-start;
    }

    .right-pack__inner {
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 8px;
    }

    .right-pack__item {
        min-width: 84px;
    }
}

@media (max-width: 600px) {
    .metric-primary {
        font-size: 2rem;
    }

    .avg-value {
        font-size: 1.75rem !important;
    }

    .metric-title {
        font-size: 0.62rem;
    }

    .laeq-compare {
        font-size: 0.58rem;
    }

    .laeq-inline {
        gap: 2px;
    }

    .chart-body {
        height: 155px;
    }

    .laeq-chart-body {
        height: 130px;
    }
}
</style>
